import type { Variant } from '../gameTypes.js';
import {
  GOOD_WINDOW_MS,
  PERFECT_WINDOW_MS,
  type Difficulty,
} from '../gameTypes.js';
import { buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import type { ChartAudio, ChartPalette } from '../chart.js';
import {
  MANIA_LANE_COUNT,
  type ManiaLane,
  type ManiaNote,
} from '../maniaNote.js';
import { type PianoChart } from '../pianoChart.js';
import {
  selectBeats,
  type RawOnsetsFile,
  type SelectBeatsOpts,
} from '../builtinPulseMaps/audioFromOnsets.js';

/**
 * Per-variant tuning for the piano renderer/engine. Difficulty rises
 * via THREE knobs simultaneously:
 *   1. `approachMs` — wall-clock reading window. Faster = less time
 *      to read the upcoming tile (independent of song tempo so
 *      muscle memory transfers between songs at different BPMs).
 *   2. `windowScale` — multiplies the global perfect/good timing
 *      windows so harder maps demand more precise taps.
 *   3. `minSpacingBeats` — beat-based density cap, see
 *      {@link PIANO_DENSITY_TUNING}.
 *
 * Audio is shared across variants — only the chart density and
 * reading speed change. Loops 2 / 3 / infinite use the engine-side
 * speed ramp from {@link import('../progression.js').loopSpeedRamp}.
 */
export const PIANO_VARIANT_TUNING: Record<
  Variant,
  { approachMs: number; windowScale: number }
> = {
  // 1300 ms — generous lookahead for the player to spot the lane
  // and pre-position. 920 / 700 are calibrated against Circles'
  // equivalents (1320 / 950 / 720) so experienced Circles players
  // step into Hard / Extreme without a visual relearn.
  normal: { approachMs: 1300, windowScale: 1.0 },
  hard: { approachMs: 920, windowScale: 0.85 },
  extreme: { approachMs: 700, windowScale: 0.7 },
};

/**
 * Per-variant note-density cap. Piano deliberately diverges from the
 * shared {@link import('../gameTypes.js').VARIANT_TUNING} multipliers
 * (which scale spacing in BEATS) because falling-tile play demands
 * consistent READING rhythm across songs — what the player feels in
 * the hands is "how many taps per second," not "how many taps per
 * beat." A beat-based cap gives a 145-BPM track 1.7× more notes/sec
 * than an 87-BPM track at the same variant; an ms-based cap
 * delivers the same notes/sec everywhere.
 *
 * Two knobs:
 *   - `minSpacingMs`: wall-clock floor between adjacent tiles. The
 *     synthesiser converts to per-chart `minSpacingBeats` via
 *     `ms × bpm / 60000`. Songs with abundant onsets get thinned to
 *     the cap; sparser songs stay at their intrinsic density.
 *   - `intensityScale`: multiplies the per-section
 *     `minIntensity` threshold. Lower values recruit weaker
 *     audio onsets as eligible candidates — the lever that lets
 *     Extreme reach its density target on tracks where Hard's
 *     candidate pool is already exhausted at the section's
 *     intensity floor.
 *
 * Calibration:
 *   - Normal 470ms ≈ 2.1 taps/sec cap (casual, single-finger reads).
 *   - Hard 290ms ≈ 3.4 taps/sec cap (two-hand alternation).
 *   - Extreme 180ms ≈ 5.5 taps/sec cap (sustained streams).
 * Intensity scales 1.0 / 0.55 / 0.25 mirror the recruitment curve
 * Circles uses at the same labels, so cross-mode muscle memory
 * transfers and per-variant chart counts grow monotonically with
 * variant on any given song.
 */
export const PIANO_DENSITY_TUNING: Record<
  Variant,
  { minSpacingMs: number; intensityScale: number }
> = {
  normal: { minSpacingMs: 470, intensityScale: 1.0 },
  hard: { minSpacingMs: 290, intensityScale: 0.55 },
  extreme: { minSpacingMs: 180, intensityScale: 0.25 },
};

/** Soft ceiling on tiles per chart from the synthesiser path. */
const SYNTH_NOTE_CAP = 1500;

/**
 * One musical region of a chart with a per-section onset filter. The
 * piano synthesiser walks these in order and produces tiles by
 * harvesting the matching beats out of the shared onset stream —
 * mirroring the Circles flow so the same chart authoring intuitions
 * (sparse verse, dense chorus, breakdown bridge, drop crescendo)
 * apply across modes.
 */
export interface PianoSectionSpec {
  startBeat: number;
  endBeat: number;
  selectOpts: SelectBeatsOpts;
}

export interface PianoChartSpec {
  baseSlug: string;
  baseTitle: string;
  description: string;
  difficulty: Difficulty;
  totalBeats: number;
  palette: ChartPalette;
  audio: ChartAudio;
  onsets: RawOnsetsFile;
  sections: ReadonlyArray<PianoSectionSpec>;
  /** Optional credit for the chart's note-pattern author. */
  mapper?: string;
}

/**
 * Assemble a PianoChart from its parts. Centralised so every builtin
 * chart wires identical approach + timing windows per variant — if
 * we later tweak the difficulty curve, only this helper changes.
 */
export interface PianoChartInit {
  baseSlug: string;
  variant: Variant;
  title: string;
  description: string;
  bpm: number;
  difficulty: Difficulty;
  totalBeats: number;
  palette: ChartPalette;
  audio: ChartAudio;
  notes: ManiaNote[];
  mapper?: string;
}

export function buildPianoChart({
  baseSlug,
  variant,
  title,
  description,
  bpm,
  difficulty,
  totalBeats,
  palette,
  audio,
  notes,
  mapper,
}: PianoChartInit): PianoChart {
  const tuning = PIANO_VARIANT_TUNING[variant];
  const slug = buildVariantSlug(baseSlug, variant);
  return {
    mode: 'piano',
    id: slug,
    slug,
    title,
    description,
    bpm,
    difficulty,
    variant,
    totalBeats,
    palette,
    audio,
    notes,
    approachMs: tuning.approachMs,
    perfectWindowMs: PERFECT_WINDOW_MS * tuning.windowScale,
    goodWindowMs: GOOD_WINDOW_MS * tuning.windowScale,
    ...(mapper !== undefined ? { mapper } : {}),
  };
}

/**
 * Lightweight deterministic PRNG seeded by chart slug. Same seed →
 * same lane assignment, so the synthesised charts are stable across
 * builds (replay verification depends on this).
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Per-variant rules used by the synthesiser. Mirrors the importer's
 * generator params but operates without MIDI pitch info — lane
 * assignment is contour-driven via the onset stream's intensity
 * trajectory rather than absolute pitch.
 */
interface SynthParams {
  maxJack: number;
  /** Probability ceiling for emitting a dual-tile companion. */
  dualCap: number;
  /**
   * Probability of converting a tap-eligible note into a hold when
   * the gap to the next onset is wide enough to host a hold body.
   * Holds break monotony on sparse stretches and feel "musical" when
   * they line up with sustained instruments (vocal lines, pad chords).
   */
  holdProb: number;
  /**
   * Minimum hold body length in beats. Sub-beat holds feel like
   * fumbled taps; we floor at this value to keep the visual ribbon
   * legible.
   */
  minHoldBeats: number;
  /**
   * Maximum hold body length in beats. Caps stretched at the upper
   * end so even on the rare 4-beat gap a hold doesn't dominate the
   * chart's reading window. Tightens with difficulty (Extreme prizes
   * speed; long holds dilute that).
   */
  maxHoldBeats: number;
  /**
   * Safety gap (beats) the synthesiser leaves between a hold's
   * release and the next onset, so the player can lift their finger
   * before the next tap fires. 0.13 beats at 120 BPM ≈ 65ms — a few
   * input frames of slack, enough to comfortably re-tap without
   * panic-mashing.
   */
  holdReleaseBufferBeats: number;
}

const SYNTH_VARIANT_PARAMS: Record<Variant, SynthParams> = {
  // Normal: rare, generous holds. Players still building muscle
  // memory get a clear, slow ribbon to follow rather than
  // competitive-density holds. Wide gaps in Normal charts are
  // abundant so the prob can stay moderate.
  normal: {
    maxJack: 1,
    dualCap: 0.05,
    holdProb: 0.55,
    minHoldBeats: 1.0,
    maxHoldBeats: 4,
    holdReleaseBufferBeats: 0.15,
  },
  // Hard: more frequent, tighter. The chart is denser so most gaps
  // are sub-beat; we accept smaller holds (0.5 beats ≈ 250ms at
  // 120 BPM) so something actually fires. Hard players are reading
  // ahead well enough to handle compact holds.
  hard: {
    maxJack: 2,
    dualCap: 0.15,
    holdProb: 0.45,
    minHoldBeats: 0.5,
    maxHoldBeats: 3,
    holdReleaseBufferBeats: 0.15,
  },
  // Extreme: pointed, hand-on-the-key holds. Even short (0.4-beat)
  // holds add a layer of input variety to streams. We cap at 2 beats
  // so they don't dilute the speed feel.
  extreme: {
    maxJack: 4,
    dualCap: 0.25,
    holdProb: 0.55,
    minHoldBeats: 0.4,
    maxHoldBeats: 2,
    holdReleaseBufferBeats: 0.12,
  },
};

interface SynthBeatEntry {
  beat: number;
  /**
   * True when this onset is a strong-intensity peak that reads as a
   * musical accent — used as the dual-tile candidate signal. The
   * onset extractor produces one onset per beat (no same-beat
   * stacking), so we treat the top intensity bucket as "chord-like"
   * accents rather than relying on multi-onset coincidence.
   */
  isAccent: boolean;
}

/**
 * Intensity threshold above which an onset is treated as a musical
 * accent eligible for dual-tile pairing. Calibrated against the NCS
 * onset distribution: ~10% of onsets sit at or above 0.5, which feeds
 * the per-variant dualCap with enough candidates to actually emit
 * the chord-accent visual.
 */
const ACCENT_INTENSITY_THRESHOLD = 0.5;

/**
 * Lane assignment for synthesised charts. Without pitch contour we
 * use a deterministic "snake" pattern that walks 0→1→2→3→2→1→0…
 * with jitter at section boundaries. The hand-balance pass enforces
 * `maxJack` so harder variants tolerate more same-lane runs.
 */
function pickLane(
  preferredLane: ManiaLane,
  recent: ReadonlyArray<ManiaLane>,
  maxJack: number,
): ManiaLane {
  let jackCount = 0;
  for (let i = recent.length - 1; i >= 0; i -= 1) {
    if (recent[i] === preferredLane) {
      jackCount += 1;
    } else {
      break;
    }
  }
  if (jackCount < maxJack) {
    return preferredLane;
  }
  // Try adjacent lanes first, then opposite side.
  const candidates: ManiaLane[] = preferredLane === 0
    ? [1, 2, 3]
    : preferredLane === 3
      ? [2, 1, 0]
      : preferredLane === 1
        ? [2, 0, 3]
        : [1, 3, 0];
  for (const candidate of candidates) {
    let candJack = 0;
    for (let i = recent.length - 1; i >= 0; i -= 1) {
      if (recent[i] === candidate) {
        candJack += 1;
      } else {
        break;
      }
    }
    if (candJack < maxJack) {
      return candidate;
    }
  }
  return preferredLane;
}

/**
 * Produce the full per-variant Record<Variant, PianoChart> for a
 * synthesised builtin track. Mirrors {@link import('../circlesBuiltinMaps/circlesFromOnsets.js').chartFromSpec}
 * — every track file calls this once and exports the resulting bundle.
 *
 * For each variant:
 *   1. Walk the spec's sections, harvesting beats via
 *      {@link variantSelectBeats} so the same onset filters used by
 *      Pulse / Circles produce the same density tier here.
 *   2. Detect chord candidates by checking whether a snare onset and
 *      a lead onset share a beat — those become eligible duals
 *      subject to the variant's dualCap.
 *   3. Walk the deduped beat stream and assign lanes via
 *      {@link pickLane}, biasing toward a snake pattern that respects
 *      `maxJack`.
 */
export function pianoVariantsFromSpec(spec: PianoChartSpec): Record<Variant, PianoChart> {
  const variants: Variant[] = ['normal', 'hard', 'extreme'];
  const out = {} as Record<Variant, PianoChart>;
  for (const variant of variants) {
    out[variant] = synthesiseVariant(spec, variant);
  }
  return out;
}

function synthesiseVariant(spec: PianoChartSpec, variant: Variant): PianoChart {
  const density = PIANO_DENSITY_TUNING[variant];
  // Convert the variant's wall-clock density cap (ms) to a per-chart
  // beat cap using this chart's BPM. Fast songs need a smaller
  // beat-gap to hit the same notes/sec ceiling — that's what gives
  // consistent reading feel across the catalogue.
  const minSpacingBeats = (density.minSpacingMs * spec.audio.bpm) / 60_000;
  const beats: SynthBeatEntry[] = [];
  for (const section of spec.sections) {
    // The piano synthesiser owns density via PIANO_DENSITY_TUNING and
    // ignores section.selectOpts.minSpacing — keeping difficulty
    // consistent across songs regardless of how many onsets each
    // track has. section.selectOpts.kinds still applies (authors
    // pick WHICH onsets are candidates); minIntensity is scaled per
    // variant so Extreme can recruit weaker onsets on tracks where
    // Hard already exhausted its candidate pool.
    const baseMinIntensity = section.selectOpts.minIntensity ?? 0;
    const sectionBeats = selectBeats(spec.onsets, {
      ...section.selectOpts,
      minIntensity: baseMinIntensity * density.intensityScale,
      minSpacing: minSpacingBeats,
      fromBeat: section.selectOpts.fromBeat ?? section.startBeat,
      untilBeat: section.selectOpts.untilBeat ?? section.endBeat,
    });
    for (const beat of sectionBeats) {
      // Tag dual-tile candidates: strong-intensity onsets read as
      // musical accents and feed the dualCap pool. The onset
      // extractor never stacks multiple onsets on the same beat, so
      // intensity is the only available chord-accent proxy here.
      const isAccent = peakIntensityAtBeat(spec.onsets, beat) >= ACCENT_INTENSITY_THRESHOLD;
      beats.push({ beat, isAccent });
    }
  }
  // Dedup + sort across sections (a section boundary onset would
  // otherwise stack two tiles).
  const seen = new Map<number, SynthBeatEntry>();
  for (const entry of beats) {
    const existing = seen.get(entry.beat);
    if (!existing || (!existing.isAccent && entry.isAccent)) {
      seen.set(entry.beat, entry);
    }
  }
  const sorted = [...seen.values()].sort((a, b) => a.beat - b.beat);

  const params = SYNTH_VARIANT_PARAMS[variant];
  const rng = mulberry32(hashSeed(`${spec.baseSlug}:${variant}`));
  const recent: ManiaLane[] = [];
  const notes: ManiaNote[] = [];
  let dualEmitted = 0;
  let direction: 1 | -1 = 1;
  let cursor: ManiaLane = 1;

  if (sorted.length > SYNTH_NOTE_CAP) {
    console.warn(
      `[piano synth] ${spec.baseSlug} ${variant}: candidate beats (${sorted.length}) exceed SYNTH_NOTE_CAP (${SYNTH_NOTE_CAP}); truncating tail. Audio plays past the last note.`,
    );
  }
  for (let i = 0; i < sorted.length && i < SYNTH_NOTE_CAP; i += 1) {
    const entry = sorted[i];

    // Snake the cursor between lanes 0..3 with occasional jitter.
    // Jittering at ~10% of beats keeps the pattern from feeling
    // mechanical without breaking muscle memory.
    if (rng() < 0.1) {
      cursor = ((cursor + (rng() < 0.5 ? 1 : -1) + 4) % 4) as ManiaLane;
    } else {
      const next = cursor + direction;
      if (next > 3 || next < 0) {
        direction = (-direction) as 1 | -1;
      }
      cursor = (cursor + direction) as ManiaLane;
      if (cursor < 0 || cursor > 3) {
        cursor = Math.max(0, Math.min(3, cursor)) as ManiaLane;
      }
    }

    const lane = pickLane(cursor, recent, params.maxJack);

    // Dual tile: cap by ratio so we don't blow past the design
    // contract on chord-heavy songs.
    const dualRatioSoFar = notes.length === 0 ? 0 : dualEmitted / notes.length;
    let dualPartnerLane: ManiaLane | undefined;
    if (entry.isAccent && dualRatioSoFar < params.dualCap && rng() < 0.6) {
      // Pair the primary lane with a non-adjacent lane for visual
      // separation. Lanes 0+2 and 1+3 read as the cleanest "left
      // hand + right hand" duals.
      const candidate: ManiaLane = ((lane + 2) % MANIA_LANE_COUNT) as ManiaLane;
      if (candidate !== lane) {
        dualPartnerLane = candidate;
        dualEmitted += 1;
      }
    }

    // Hold conversion: when the gap to the next onset is wide enough
    // to fit a hold body plus a release buffer, roll the holdProb dice
    // and convert this tap into a hold. Holds skip dual tiles in v1 —
    // synchronised release across two fingers is an extra cognitive
    // load that doesn't pay off on the synthesised charts; holds stay
    // single-finger so the player only has to time one release.
    let holdEndBeat: number | undefined;
    if (dualPartnerLane === undefined && i + 1 < sorted.length) {
      const nextBeat = sorted[i + 1].beat;
      const gap = nextBeat - entry.beat;
      const usableTail = gap - params.holdReleaseBufferBeats;
      if (usableTail >= params.minHoldBeats && rng() < params.holdProb) {
        const length = Math.min(usableTail, params.maxHoldBeats);
        holdEndBeat = entry.beat + length;
      }
    }

    const note: ManiaNote = {
      id: `n-${i.toString().padStart(4, '0')}`,
      beat: entry.beat,
      lane,
    };
    if (dualPartnerLane !== undefined) {
      note.dualPartnerLane = dualPartnerLane;
    }
    if (holdEndBeat !== undefined) {
      note.holdEndBeat = holdEndBeat;
    }
    notes.push(note);
    recent.push(lane);
    if (recent.length > 8) {
      recent.shift();
    }
  }

  return buildPianoChart({
    baseSlug: spec.baseSlug,
    variant,
    title: buildVariantTitle(spec.baseTitle, variant),
    description: spec.description,
    bpm: spec.audio.bpm,
    difficulty: spec.difficulty,
    totalBeats: spec.totalBeats,
    palette: spec.palette,
    audio: spec.audio,
    notes,
    mapper: spec.mapper,
  });
}

const SAME_BEAT_EPSILON = 1e-6;

/**
 * Highest intensity reported across all onsets that land exactly on
 * `beat`. Onsets are pre-sorted by beat in the source files so we
 * exit early once we pass the target.
 */
function peakIntensityAtBeat(onsets: RawOnsetsFile, beat: number): number {
  let peak = 0;
  for (const o of onsets.onsets) {
    if (Math.abs(o.beat - beat) < SAME_BEAT_EPSILON) {
      if (o.intensity > peak) {
        peak = o.intensity;
      }
    }
    if (o.beat > beat + SAME_BEAT_EPSILON) {
      break;
    }
  }
  return peak;
}
